import React from 'react';
import { copyTextToClipboard } from '../../utilities/clipboard';

export type CopyOutcome = 'copied' | 'failed';

interface ShownCopyResult {
    owner: object;
    outcome: CopyOutcome;
}

const COPIED_DURATION_MS = 2000;

// Screen readers merge a clear and a refill that come too close together, so
// a repeated "Copied" would look unchanged and stay silent.
export const ANNOUNCE_DELAY_MS = 100;

const pause = (ms: number) =>
    new Promise(resolve => window.setTimeout(resolve, ms));

// One result on screen at a time across the app, so a newer copy replaces
// the message of an older one instead of stacking stale confirmations.
const listeners = new Set<(result: ShownCopyResult | null) => void>();

const showResult = (result: ShownCopyResult | null) =>
    listeners.forEach(listener => listener(result));

// Only the newest attempt may show a result; copies can finish out of order.
let latestAttempt: { owner: object } | undefined;

/** Copies text and reports the outcome only to the caller that copied last. */
export const useCopyToClipboard = (text: string) => {
    const [owner] = React.useState(() => ({}));
    const [outcome, setOutcome] = React.useState<CopyOutcome>();
    const revertTimeoutRef = React.useRef<number>();

    React.useEffect(
        () => () => {
            window.clearTimeout(revertTimeoutRef.current);
            // A copy still running when the button goes away shows nothing.
            latestAttempt =
                latestAttempt?.owner === owner ? undefined : latestAttempt;
        },
        [owner],
    );

    React.useEffect(() => {
        const listener = (result: ShownCopyResult | null) =>
            setOutcome(result?.owner === owner ? result.outcome : undefined);
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
        };
    }, [owner]);

    const copy = React.useCallback(async () => {
        // Cleared first: an unchanged live region announces nothing, so a
        // repeated copy has to change it, and stay empty long enough to be noticed.
        window.clearTimeout(revertTimeoutRef.current);
        showResult(null);
        const attempt = { owner };
        latestAttempt = attempt;
        const [copied] = await Promise.all([
            copyTextToClipboard(text).catch(() => false),
            pause(ANNOUNCE_DELAY_MS),
        ]);
        if (attempt !== latestAttempt) {
            return;
        }
        showResult({ owner, outcome: copied ? 'copied' : 'failed' });
        // The check icon goes back to the copy icon; a failure message stays.
        revertTimeoutRef.current = copied
            ? window.setTimeout(() => setOutcome(undefined), COPIED_DURATION_MS)
            : undefined;
    }, [owner, text]);

    return { copy, outcome };
};
